import { Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { IvvGateChip } from '@/components/ui/ivv-gate-chip';
import { formatDate, titleCase } from '@/lib/utils/format';
import type { DeliverableRevision } from '@/lib/api/change-orders';

/**
 * ChangeCard — T-0102 / E06-F02 §1.
 *
 * Per-change diff card on the Changes screen. For each SOW version in the
 * supersede chain, this renders:
 *
 *   - Header strip: version label + effective date + status chip
 *   - Summary line: "X added · Y modified · Z removed" computed from the
 *     revisions array
 *   - Per-revision rows: change_type chip + register_entry_id (linked to
 *     deliverables tab) + effective range + the revised_payload diff
 *     (rendered as a compact JSON snippet — full diff lives in the audit
 *     trail per CLAUDE.md §7)
 *   - "View full SOW at this version" link — v1 placeholder per ticket §2
 *     ("just shows version metadata"); a future ticket wires a snapshot
 *     viewer that resolves the as_of view at the SOW's effective_date.
 *
 * Tone mapping (matches prototype `changeOrderCard` color scheme):
 *   - status='current'    → posture-healthy accent
 *   - status='superseded' → accent-amber (the prior versions in the chain)
 *   - Any other status    → ink-300 fallback
 */

export interface ChangeCardProps {
  sow_id: string;
  versionLabel: string;
  effectiveDate: string;
  status: string;
  /** Total revisions applied against this SOW. */
  revisions: ReadonlyArray<DeliverableRevision>;
  /** Optional link target for "View full SOW at this version". */
  engagementId: string;
  /** Optional "open the as-of EDL view" callback. */
  onViewAsOf?: (effectiveDate: string) => void;
  /**
   * Undo this registration (C2-51). The page passes it for the chain head
   * only (the current version above the baseline); the confirm step and
   * the request are the page's.
   */
  onRemove?: () => void;
  className?: string;
}

const TYPE_TONE: Record<string, string> = {
  added: 'bg-posture-healthy-tint text-posture-healthy',
  modified: 'bg-posture-at-risk-tint text-posture-at-risk',
  removed: 'bg-posture-critical-tint text-posture-critical',
};

export function ChangeCard({
  sow_id,
  versionLabel,
  effectiveDate,
  status,
  revisions,
  onRemove,
  className,
}: ChangeCardProps) {
  const counts = revisions.reduce(
    (acc, rev) => {
      const key = rev.change_type as 'added' | 'modified' | 'removed';
      if (key === 'added' || key === 'modified' || key === 'removed') acc[key] += 1;
      return acc;
    },
    { added: 0, modified: 0, removed: 0 },
  );

  const accent =
    status === 'current'
      ? 'border-l-posture-healthy'
      : status === 'superseded'
        ? 'border-l-accent-amber-600'
        : 'border-l-ink-300';

  const statusToneClass =
    status === 'current'
      ? 'bg-posture-healthy-tint text-posture-healthy'
      : status === 'superseded'
        ? 'bg-paper-100 text-ink-700'
        : 'bg-ink-100 text-ink-500';

  return (
    <Card className={cn('overflow-hidden border-l-[3px]', accent, className)}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <CardTitle className="text-h3 font-semibold text-ink-900">SOW {versionLabel}</CardTitle>
            <span className="font-mono text-mono-sm text-ink-500">
              effective {formatDate(`${effectiveDate}T00:00:00.000Z`)}
            </span>
            <span
              className={cn(
                'inline-flex h-[22px] items-center rounded-full px-2.5 text-[12px] font-medium leading-[18px]',
                statusToneClass,
              )}
            >
              {titleCase(status)}
            </span>
          </div>
          {onRemove ? (
            <Button type="button" variant="outline" size="sm" onClick={onRemove}>
              <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
              Remove this version
            </Button>
          ) : null}
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-meta">
          <Pill tone="added">{counts.added} added</Pill>
          <Pill tone="modified">{counts.modified} modified</Pill>
          <Pill tone="removed">{counts.removed} removed</Pill>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {revisions.length === 0 ? (
          <p className="text-body-sm text-ink-500">
            No deliverable revisions applied at this version.
          </p>
        ) : (
          <ul role="list" className="divide-y divide-ink-100">
            {revisions.map((rev) => (
              <RevisionRow key={rev.id} rev={rev} />
            ))}
          </ul>
        )}
        <p className="pt-2 text-meta text-ink-500">
          Each revision is recorded in the engagement audit trail with who approved it.
        </p>
      </CardContent>
    </Card>
  );
}

function Pill({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'added' | 'modified' | 'removed';
}) {
  return (
    <span
      className={cn(
        'inline-flex h-[18px] items-center rounded-chip px-2 text-[11px] font-medium',
        TYPE_TONE[tone] ?? 'bg-ink-100 text-ink-700',
      )}
    >
      {children}
    </span>
  );
}

const FIELD_LABELS: Record<string, string> = {
  title: 'Name',
  name: 'Name',
  description: 'Description',
  expected_delivery_date: 'Due date',
  acceptance_criteria: 'Acceptance criteria',
  sow_ref: 'SOW reference',
  is_payment_tied: 'Tied to payment',
  is_ivv_gate: 'IV&V gate',
  item_type: 'Type',
};

/**
 * A milestone line whose revised payload names it as an IV&V gate (E19 /
 * T-0218) carries the chip instead of a field line. The flag means nothing
 * on another item type, so a payload that says so is left to the field
 * list.
 */
function revisedAsIvvGate(payload: unknown): boolean {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return false;
  const p = payload as Record<string, unknown>;
  return p.is_ivv_gate === true && (p.item_type === undefined || p.item_type === 'milestone');
}

/** One readable "Field → new value" line per changed field. */
function describeChanges(payload: unknown): Array<{ label: string; value: string }> {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return [];
  const out: Array<{ label: string; value: string }> = [];
  for (const [key, raw] of Object.entries(payload as Record<string, unknown>)) {
    if (raw === null || raw === undefined) continue;
    const label = FIELD_LABELS[key] ?? titleCase(key.replace(/_/g, ' '));
    const value = Array.isArray(raw)
      ? raw.map(String).join('; ')
      : typeof raw === 'boolean'
        ? raw
          ? 'Yes'
          : 'No'
        : typeof raw === 'object'
          ? ''
          : String(raw);
    if (value.length > 0) out.push({ label, value });
  }
  return out;
}

function RevisionRow({ rev }: { rev: DeliverableRevision }) {
  const tone = TYPE_TONE[rev.change_type] ?? 'bg-ink-100 text-ink-700';
  const changes = describeChanges(rev.revised_payload);
  const ivvGate = revisedAsIvvGate(rev.revised_payload);
  // For an added deliverable the name is in the payload itself.
  const title =
    rev.deliverable_title ??
    changes.find((c) => c.label === 'Name')?.value ??
    'Unnamed deliverable';
  const fields =
    rev.change_type === 'removed'
      ? []
      : changes.filter(
          (c) =>
            !(c.label === 'Name' && c.value === title) &&
            !(ivvGate && c.label === FIELD_LABELS.is_ivv_gate),
        );

  return (
    <li className="py-2.5">
      <div className="flex flex-wrap items-baseline gap-2">
        <span
          className={cn(
            'inline-flex h-[22px] items-center rounded-full px-2.5 text-[12px] font-medium leading-[18px]',
            tone,
          )}
        >
          {titleCase(rev.change_type)}
        </span>
        <span className="text-body-sm font-medium text-ink-900">{title}</span>
        {ivvGate ? <IvvGateChip /> : null}
        <span className="text-meta text-ink-500">from {formatDate(rev.effective_start)}</span>
      </div>
      {fields.length > 0 ? (
        <dl className="mt-1.5 space-y-0.5 text-body-sm">
          {fields.map((f) => (
            <div key={f.label} className="flex gap-2">
              <dt className="w-36 shrink-0 text-ink-500">
                {rev.change_type === 'modified' ? `${f.label} is now` : f.label}
              </dt>
              <dd className="min-w-0 break-words text-ink-900">{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {rev.rationale ? <p className="mt-1 text-meta text-ink-500">Why: {rev.rationale}</p> : null}
    </li>
  );
}
